import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useBranding } from "@/lib/branding";
import {
  ArrowRight,
  Building2,
  Check,
  Play,
  Scale,
  Sparkles,
  Volume2,
  VolumeX,
} from "lucide-react";

/**
 * `/tax-pros` — Seasoned preparer landing.
 * Structure (per user 2026-02-09):
 *   1. Video hero (Sora 2 generated) with caption overlay
 *   2. 1120 + AFSP cards side-by-side (the two courses grouped together)
 *   3. White-Label section featured separately below
 */
export default function TaxPros() {
  const { branding } = useBranding();
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [videoBroken, setVideoBroken] = useState(false);

  const play = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    setMuted(false);
    v.play().then(() => setPlaying(true)).catch(() => {
      // Autoplay-with-sound blocked — fall back to muted playback.
      v.muted = true;
      setMuted(true);
      v.play().then(() => setPlaying(true)).catch(() => {});
    });
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="tax-pros-page">
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto p-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="" className="h-9 w-9 object-contain" />
            ) : (
              <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground grid place-items-center font-serif">
                {branding.brand_name?.[0]}
              </div>
            )}
            <div className="font-serif text-2xl">{branding.brand_name}</div>
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/" className="hidden sm:inline underline text-muted-foreground hover:text-foreground">Home</Link>
            <Link to="/become-a-tax-pro" className="hidden md:inline underline text-muted-foreground hover:text-foreground">Become a Tax Pro</Link>
          </nav>
        </div>
      </header>

      {/* VIDEO HERO */}
      <section className="relative bg-gradient-to-b from-slate-950 to-slate-900" data-testid="tp-video-hero">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-4 py-1.5 text-accent text-xs uppercase tracking-widest mb-5">
              <Sparkles size={13} /> For seasoned tax preparers
            </div>
            <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] text-white mb-4">
              Tax pros, need a refresher? <span className="italic text-accent">We got you.</span>
            </h1>
            <p className="text-white/70 max-w-xl mx-auto">
              1120, AFSP, or your own white-label program — watch.
            </p>
          </div>

          <div className="relative rounded-xl overflow-hidden border border-white/10 shadow-2xl bg-black aspect-video">
            <video
              ref={videoRef}
              src="/tax-pros.mp4"
              playsInline
              muted={muted}
              onEnded={() => setPlaying(false)}
              onError={() => setVideoBroken(true)}
              className={`w-full h-full object-cover ${videoBroken ? "hidden" : ""}`}
              data-testid="tp-hero-video"
            />

            {/* Caption strip — always visible while playing, keeps the message on-screen */}
            {playing && !videoBroken && (
              <div className="absolute inset-x-0 bottom-0 p-4 md:p-6 bg-gradient-to-t from-black/90 via-black/60 to-transparent pointer-events-none" data-testid="tp-hero-video-caption">
                <div className="max-w-2xl mx-auto text-center text-white">
                  <div className="font-serif text-lg md:text-2xl leading-snug">
                    Tax pros, need a refresher? We got you — 1120, AFSP, or your own white-label program.
                  </div>
                </div>
              </div>
            )}

            {/* Fallback if the file is missing / can't decode (headless browsers) */}
            {videoBroken && (
              <div className="absolute inset-0 grid place-items-center text-center text-white/80 p-8" data-testid="tp-hero-video-fallback">
                <div>
                  <div className="font-serif text-3xl md:text-5xl mb-2">Tax pros, need a refresher? We got you.</div>
                  <div className="text-xs uppercase tracking-[0.35em] text-white/50">1120 · AFSP · Or your own white-label</div>
                </div>
              </div>
            )}

            {/* Play overlay before first play */}
            {!playing && !videoBroken && (
              <button
                onClick={play}
                className="absolute inset-0 grid place-items-center bg-black/50 hover:bg-black/40 transition-colors group"
                data-testid="tp-hero-video-play"
                aria-label="Play video"
              >
                <span className="h-20 w-20 rounded-full bg-white/95 text-slate-900 grid place-items-center shadow-2xl group-hover:scale-105 transition-transform">
                  <Play size={32} className="ml-1" fill="currentColor" />
                </span>
              </button>
            )}

            {/* Mute toggle while playing */}
            {playing && !videoBroken && (
              <button
                onClick={toggleMute}
                className="absolute top-4 right-4 h-11 w-11 rounded-full bg-black/60 backdrop-blur-sm text-white grid place-items-center hover:bg-black/80 transition-colors"
                data-testid="tp-hero-video-mute"
                aria-label={muted ? "Unmute" : "Mute"}
              >
                {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* 1120 + AFSP — GROUPED TOGETHER */}
      <section id="offerings" className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16">
          <div className="text-center mb-10">
            <div className="label-caps mb-3 text-primary">Continuing education</div>
            <h2 className="font-serif text-4xl md:text-5xl">
              Sharpen your craft. <span className="italic text-primary">Grow your credentials.</span>
            </h2>
            <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">
              Two focused programs for working preparers — pick one, or bundle both.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6" data-testid="tp-offerings-grid">
            <OfferingCard
              testid="tp-offer-1120"
              icon={<Building2 size={26} />}
              tag="1120 Refresher"
              price="$250"
              title="Mastering Form 1120"
              body="13 chapters covering every line of the 1120 — depreciation, DRD, Schedules L / M-1 / M-2, PHC & AET traps, plus practice returns on the actual form line numbers."
              features={[
                "13 in-depth chapters",
                "Practice returns on real form lines",
                "§179, bonus depreciation, DRD",
                "Final exam + printable certificate",
                "Free preview: Chapters 1–3",
              ]}
              cta="See 1120 details"
              to="/1120-refresher"
              highlight={false}
            />
            <OfferingCard
              testid="tp-offer-afsp"
              icon={<Scale size={26} />}
              tag="AFSP Prep Bundle"
              price="$199"
              title="AFTR Exam Prep"
              body="Circular 230 study guide (highlighted PDF) + 100-question AFTR practice simulation + 18-question quick quiz + printable certificate on pass."
              features={[
                "Full IRS Circular 230 PDF",
                "100-Q, 3-hour full simulation",
                "18-Q quick practice quiz",
                "Rationale after every answer",
                "Auto-issued certificate on pass",
              ]}
              cta="See AFSP details"
              to="/afsp-bundle"
              highlight={true}
              badge="Most popular"
            />
          </div>
        </div>
      </section>

      {/* WHITE-LABEL — SEPARATE FEATURED SECTION */}
      <section
        className="border-t border-border relative overflow-hidden"
        style={{
          background:
            "radial-gradient(ellipse at 20% 20%, rgba(46,79,184,0.35) 0%, rgba(15,23,42,0.98) 55%, #0a0f1f 100%)",
        }}
        data-testid="tp-whitelabel-section"
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-20 grid md:grid-cols-5 gap-10 items-center">
          <div className="md:col-span-3">
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/90 text-xs font-medium">
              <Sparkles size={13} className="text-accent" /> Ready to launch your own?
            </div>
            <h2 className="font-serif text-4xl md:text-6xl leading-none text-white mb-5">
              White-Label the <span className="italic bg-clip-text text-transparent bg-gradient-to-r from-blue-300 via-white to-blue-300">entire platform.</span>
            </h2>
            <p className="text-lg text-white/70 mb-6 max-w-xl">
              This whole system — intake forms, client portal, booking calendar, courses, AI helper — but with <em>your</em> logo, <em>your</em> colors, and <em>your</em> business name. 100% owned by you.
            </p>
            <ul className="space-y-2 mb-8 text-sm text-white/80">
              {WHITE_LABEL_FEATURES.map((f, i) => (
                <li key={i} className="flex items-start gap-2" data-testid={`tp-wl-feature-${i}`}>
                  <Check size={14} className="mt-1 shrink-0 text-accent" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="bg-white text-slate-900 hover:bg-white/90" data-testid="tp-whitelabel-cta">
                <Link to="/white-label">See White-Label details <ArrowRight size={16} className="ml-2" /></Link>
              </Button>
              <div className="flex items-baseline gap-2 text-white">
                <span className="text-white/50 line-through text-sm">$997</span>
                <span className="font-serif text-3xl">$699</span>
                <span className="text-xs text-white/60">/ one-time</span>
              </div>
            </div>
          </div>
          <div className="md:col-span-2">
            <div className="relative rounded-lg border border-white/20 bg-white/[0.03] backdrop-blur-sm p-6 text-white">
              <div className="flex items-center gap-3 pb-4 border-b border-white/10 mb-4">
                <div className="h-9 w-9 rounded-md bg-accent grid place-items-center font-serif text-lg text-accent-foreground">Y</div>
                <div>
                  <div className="font-serif text-sm">Your Tax Business</div>
                  <div className="text-[10px] uppercase tracking-wide text-white/60">Client Intake Form</div>
                </div>
              </div>
              <div className="space-y-2">
                {["Full name", "Email address", "Mailing address"].map((label) => (
                  <div key={label} className="border border-white/20 rounded px-3 py-2 text-xs text-white/70">
                    {label}
                  </div>
                ))}
              </div>
              <div className="mt-3 h-1 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full rounded-full bg-accent" style={{ width: "75%" }} />
              </div>
              <div className="mt-2 text-[10px] text-white/60 text-right">Powered by {branding.brand_name}</div>
            </div>
          </div>
        </div>
      </section>

      {/* PROFESSIONAL TAX SOFTWARE — AFFILIATE PROGRAM */}
      <section className="border-t border-border bg-secondary/30" data-testid="tp-affiliate-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-20">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-primary text-xs uppercase tracking-widest mb-4">
              <SoftwareIcon /> Professional tax software · Join the team
            </div>
            <h2 className="font-serif text-4xl md:text-5xl mb-3">
              Prep taxes under <span className="italic text-primary">DyNamic Tax.</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              If you want to join our team and need professional software, here's how the splits break down. Preparer always receives the larger share of the split.
            </p>
          </div>

          {/* VIDEO HERO — Sora 2 generated */}
          <div className="max-w-4xl mx-auto mb-10">
            <AffiliateVideoPlayer />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="tp-tier-grid">
            {AFFILIATE_TIERS.map((t) => (
              <TierCard key={t.key} {...t} />
            ))}
          </div>

          {/* What's included */}
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <IncludedCard title="TaxSlayer Pro" body="Full professional preparation software with e-file included for every return." />
            <IncludedCard title="Bank Products" body="Refund Advantage bank products — cash advances included on approved returns." />
            <IncludedCard title="Mentorship" body="One-on-one guidance from Di'Meka included in every package, at every tier." />
          </div>

          {/* Sale + CTA */}
          <div className="rounded-xl border-2 border-accent bg-accent/10 p-6 lg:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-8" data-testid="tp-software-sale">
            <div>
              <div className="label-caps mb-1 text-accent">Limited-time launch price</div>
              <div className="flex items-baseline gap-3 mb-2">
                <span className="font-serif text-4xl">$150</span>
                <span className="text-muted-foreground line-through">$300</span>
              </div>
              <div className="text-sm text-muted-foreground">
                Software is $150 <strong className="text-foreground">until October 31st</strong>. Regular price $300 thereafter.
              </div>
            </div>
            <Button asChild size="lg" data-testid="tp-affiliate-apply-cta">
              <Link to="/affiliate/apply">Sign the contract & apply <ArrowRight size={16} className="ml-2" /></Link>
            </Button>
          </div>

          {/* Disclaimer */}
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-5 text-sm leading-relaxed" data-testid="tp-affiliate-disclaimer">
            <div className="label-caps text-[10px] mb-2 text-destructive">Important disclaimer</div>
            <p className="mb-2">
              If you are found to be doing any <strong>fraudulent activities</strong>, you will be terminated from the software and <strong>{branding.brand_name || "DyNamic Tax"} is not responsible for any bank fees</strong> that come in after dismissal.
            </p>
            <p>
              After the <strong>first return is submitted</strong> through the software, there are <strong>no refunds</strong> issued.
            </p>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 py-8 text-sm text-muted-foreground flex flex-col sm:flex-row items-center justify-between gap-3">
          <div>Powered by <span className="text-foreground">{branding.brand_name}</span></div>
          <div className="flex gap-4">
            <Link to="/become-a-tax-pro" className="underline hover:text-foreground">Become a Tax Pro</Link>
            <Link to="/affiliate/apply" className="underline hover:text-foreground">Affiliate application</Link>
            <Link to="/" className="underline hover:text-foreground">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

const AFFILIATE_TIERS = [
  { key: "efin_100_plus",    tier: "EFIN · 100+ returns",  split: "90 / 10", body: "EFIN holders with 100 or more funded returns last season." },
  { key: "efin_under_100",   tier: "EFIN · under 100",     split: "80 / 20", body: "EFIN holders with under 100 funded returns last season." },
  { key: "ptin_2yr_50_plus", tier: "PTIN 2+ yr · 50+",     split: "70 / 30", body: "PTIN holders 2+ years with 50 or more funded returns." },
  { key: "ptin_2yr_under_50",tier: "PTIN 2+ yr · <50",     split: "60 / 40", body: "PTIN holders 2+ years with under 50 funded returns." },
  { key: "ptin_first_year",  tier: "PTIN · first year",    split: "60 / 40", body: "First-year PTIN preparers. Mentorship included." },
];

function TierCard({ tier, split, body }) {
  return (
    <div className="p-5 rounded-lg border border-border bg-card flex flex-col">
      <div className="label-caps mb-1 text-primary">{tier}</div>
      <div className="flex items-baseline gap-2 mb-2">
        <span className="font-serif text-3xl">{split}</span>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">split</span>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">{body}</p>
    </div>
  );
}

function IncludedCard({ title, body }) {
  return (
    <div className="p-5 rounded-lg border border-border bg-card">
      <div className="flex items-center gap-2 mb-2">
        <Check size={16} className="text-primary" />
        <div className="font-serif text-lg">{title}</div>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">{body}</p>
    </div>
  );
}

function SoftwareIcon() { return <Sparkles size={13} />; }

function AffiliateVideoPlayer() {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [videoBroken, setVideoBroken] = useState(false);

  const play = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    setMuted(false);
    v.play().then(() => setPlaying(true)).catch(() => {
      v.muted = true; setMuted(true);
      v.play().then(() => setPlaying(true)).catch(() => {});
    });
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted; setMuted(v.muted);
  };

  return (
    <div className="relative rounded-xl overflow-hidden border border-border shadow-2xl bg-black aspect-video" data-testid="tp-affiliate-video-wrap">
      <video
        ref={videoRef}
        src="/affiliate-software.mp4"
        playsInline
        muted={muted}
        onEnded={() => setPlaying(false)}
        onError={() => setVideoBroken(true)}
        className={`w-full h-full object-cover ${videoBroken ? "hidden" : ""}`}
        data-testid="tp-affiliate-video"
      />

      {/* Caption strip while playing */}
      {playing && !videoBroken && (
        <div className="absolute inset-x-0 bottom-0 p-4 md:p-6 bg-gradient-to-t from-black/90 via-black/60 to-transparent pointer-events-none" data-testid="tp-affiliate-video-caption">
          <div className="max-w-2xl mx-auto text-center text-white">
            <div className="font-serif text-lg md:text-2xl leading-snug">
              Looking for professional tax software? Look no further.
            </div>
          </div>
        </div>
      )}

      {/* Fallback for headless browsers */}
      {videoBroken && (
        <div className="absolute inset-0 grid place-items-center text-center text-white/80 p-8" data-testid="tp-affiliate-video-fallback">
          <div>
            <div className="font-serif text-3xl md:text-5xl mb-2">Looking for pro tax software?</div>
            <div className="text-xs uppercase tracking-[0.35em] text-white/50">Look no further</div>
          </div>
        </div>
      )}

      {/* Play button before first play */}
      {!playing && !videoBroken && (
        <button
          onClick={play}
          className="absolute inset-0 grid place-items-center bg-black/50 hover:bg-black/40 transition-colors group"
          data-testid="tp-affiliate-video-play"
          aria-label="Play video"
        >
          <span className="h-20 w-20 rounded-full bg-white/95 text-slate-900 grid place-items-center shadow-2xl group-hover:scale-105 transition-transform">
            <Play size={32} className="ml-1" fill="currentColor" />
          </span>
        </button>
      )}

      {/* Mute toggle while playing */}
      {playing && !videoBroken && (
        <button
          onClick={toggleMute}
          className="absolute top-4 right-4 h-11 w-11 rounded-full bg-black/60 backdrop-blur-sm text-white grid place-items-center hover:bg-black/80 transition-colors"
          data-testid="tp-affiliate-video-mute"
          aria-label={muted ? "Unmute" : "Mute"}
        >
          {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>
      )}
    </div>
  );
}

function OfferingCard({ testid, icon, tag, price, title, body, features, cta, to, highlight, badge }) {
  return (
    <div
      className={`p-6 lg:p-8 rounded-lg border-2 flex flex-col relative ${highlight ? "border-primary bg-primary/5 shadow-lg" : "border-border bg-card"}`}
      data-testid={testid}
    >
      {badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[10px] font-semibold tracking-widest uppercase px-3 py-1 rounded-full whitespace-nowrap">
          {badge}
        </div>
      )}
      <div className={`h-14 w-14 rounded-md ${highlight ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"} grid place-items-center mb-4`}>
        {icon}
      </div>
      <div className="label-caps mb-1 text-primary">{tag}</div>
      <h3 className="font-serif text-2xl mb-3">{title}</h3>
      <div className="mb-4">
        <div className="flex items-baseline gap-2">
          <span className="font-serif text-4xl">{price}</span>
        </div>
        <div className="text-xs text-muted-foreground">One-time · lifetime access</div>
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed mb-4">{body}</p>
      <ul className="space-y-2 mb-6 flex-1">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <Check size={13} className="mt-1 shrink-0 text-primary" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Button asChild variant={highlight ? "default" : "outline"} className="w-full" data-testid={`${testid}-btn`}>
        <Link to={to}>{cta} <ArrowRight size={14} className="ml-2" /></Link>
      </Button>
    </div>
  );
}

const WHITE_LABEL_FEATURES = [
  "Complete platform rebrand — your logo, colors, and business name",
  "Branded intake forms, client portal, and admin dashboard",
  "Instructor classes and courses under your practice",
  "Fulfillment wizard included — setup guided end-to-end",
  "One-time introductory launch price · 100% ownership",
];
